import dateTime from "@/components/CX/Grid/dateTime";
import moment from "moment";
import jpath from 'jsonpath';
import Thresholds from '@/mixins/ThresholdColor';
import DashboardService from "@/services/DashboardService";

// Renderer
import AddressCellRenderer from '@/components/CX/Grid/AddressCellRenderer';
import LinkCellRenderer from '@/components/CX/Grid/LinkCellRenderer';
import ImageCellRenderer from "@/components/CX/Grid/ImageCellRenderer";
import CXScoreCellRenderer from "@/components/CX/Grid/CXScoreCellRenderer";
import HourCellRenderer from "@/components/CX/Grid/HourCellRenderer";
import PercentCellRenderer from "@/components/CX/Grid/PercentCellRenderer";
import StarCellRenderer from "@/components/CX/Grid/StarCellRenderer";
import FlagCellRenderer from '@/components/CX/Grid/FlagCellRenderer';

// Filter
import AccomplishedFilter from '@/components/CX/Grid/AccomplishedFilter';

// Tooltips
import ThresholdTooltip from '@/components/CX/Grid/ThresholdTooltip';

export default {
  mixins: [ Thresholds ],
  
  data() {
    return {
      customColumns: { 
        active: [],
        items: []
      },
      tableCancelToken: null,
      canHaveAccomplished: false      
    }
  },

  components: { 
    addressCellRenderer: AddressCellRenderer,
    imageCellRenderer: ImageCellRenderer,
    linkCellRenderer: LinkCellRenderer,
    cxScoreCellRenderer: CXScoreCellRenderer,
    percentCellRenderer: PercentCellRenderer,
    starCellRenderer: StarCellRenderer,
    hourCellRenderer: HourCellRenderer,
    flagCellRenderer: FlagCellRenderer,
    accomplishedFilter: AccomplishedFilter,
    thresholdTooltip: ThresholdTooltip
  },
  
  methods: {
    getExcelSettings() {
      return {
        processCellCallback: params => {
          const rowIndex = params.accumulatedRowIndex;
          const coldDef = params.column.getColDef();
          switch(coldDef.cellRenderer) {
            case "imageCellRenderer": return "";
            case "linkCellRenderer": return "";
            case "percentCellRenderer": return params.value ? `${params.value}%` : "";
            case "hourCellRenderer": 
              if (params.value === 0) return '0:00h';
              else if (params.value === null) return 'N/A';
              else return params.value 
                ? dateTime.formatHoursMinutes(params.value)
                : '';

            case "starCellRenderer": return params.value ? `${params.value.toFixed(1)} Stars` : "";
            default: return params.value;
          }
        },
        author: "MDatiqs GmbH",
        sheetName: "1",
        allColumns: true,
        fileName: `CX_${new Date().getFullYear()}_${new Date().getMonth()}_${new Date().getDate()}_${new Date().getHours()}_${new Date().getMinutes()}_${new Date().getSeconds()}`
      };
    },

    applyTopLineFix(inputLine, hideLink = false) {
      if (!inputLine) return [];

      let line;
      try {
        line = JSON.parse(JSON.stringify(inputLine));
        if (hideLink)
          line.hideLink = hideLink;

        for (let i = 0; i < line.results.length; i++) {
          const item = line.results[i];
          
          let nulls = item.results.filter(x => x.result === null) || [];
          if (nulls.length > 0) {
            for (let j = 0; j < item.results.length; j++) {
              const sub = item.results[j];
              if (sub.result === null)
                sub.result = "";
            }
          }
        }
      } catch (error) {
        line = inputLine
      }
      return [line];
    },

    exportExcel(params) {
      // backup active dynamic columns
      let backupActive = JSON.parse(JSON.stringify(this.customColumns.active));
      if (params.mode == 'all') {
        // activate all keys
        this.customColumns.active = this.customColumns.items
          .filter(x => !x.isHeader)
          .map(x => x.key);
      }

      // Wait for new Render
      this.$nextTick(() => { 
        var columns = this.gridColumnApi.getColumns();
        columns.splice(0, 2);
        this.gridApi.exportDataAsExcel({columnKeys: columns});
        // reset active keys to previous state
        if (params.mode == 'all') this.customColumns.active = backupActive;
      });
    },

    setupTableStructure(columns) {
      // #1673 ... temproary fix in frontend
      let start = _.slice(columns, 0, 3);
      let rest = _.slice(columns, 3);
      let x = JSON.parse(JSON.stringify(_.last(start)));
      x.group = "Exploration3";
      start.push(x);
      columns = start.concat(rest);

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];

        col.headerName = col.text;

        // #1673 ... temproary fix in frontend
        if (col.group === "Exploration2") {
          if (col.key.includes("Web Presence") || col.key.includes("Social Media")) {
            col.group = "Exploration3";
          }
        }

        col.key = `${i}`;

        if (!_.isEmpty(col.thresholds)) {
          for (let j = 0; j < col.thresholds.length; j++) {
            const th = col.thresholds[j];
            if (_.isEmpty(th.blabla)) {
              th.niceText = this.getThresholdText(columns, th, '%', ' - ');
            }
          }
        }
      }
      return columns;
    },

    generateColumns(count, renderer, valueFormat, dateFormat, firstColHeader = "Current Value") {
      let items = [];
      for (let i = 0; i < count; i++) {
        items.push(
          {
            show: i === 0 ? "always" : "open",
            render: renderer,
            format: valueFormat,
            filter: true,
            text: i === 0 ? firstColHeader : moment().subtract(i, "month").format(dateFormat),
            headerName: i === 0 ? firstColHeader : moment().subtract(i, "month").format(dateFormat)
          }
        );
      }
      return items;
    },

    percentValueFormatter(params) {
      return params.value ? params.value + "%" : "";
    },
    
    hourValueFormatter(params) {
      return dateTime.formatHoursMinutes(params.value);
    },

    avgAggregation(params) {
      let elements = params.values.filter(x => x !== undefined && x !== null)
      let sum = elements.reduce((a, b) => a + b, 0);
      
      return elements.length > 0 ? (sum / elements.length) : null;
    },

    mixinGetColumn(arg) {
      let item = this.customColumns.items.find(x => x.key == arg);
      if (!item) return;
      if (item?.isHeader) {
        return;
      }

      if (!item?.path) {
        return;
      }

      this.setupColumnDefinitionsIfMissing(item);

      return this.setupMultiColumns(item, 
        { headerName: item.text, children: [] });
    },

    setupColumnDefinitionsIfMissing(item) {
      if (item.columns && item.columnNames)
        return;
      
      item.columns = this.generateColumns(
        item.columnCount, item.render, item.format, 'MMMM YYYY');

      if (item.columnNames) {
        for (let i = item.columnNames.length-1; i >= 0; i--) {
          item.columns[i].text = item.columnNames[i];
          item.columns[i].headerName = item.columnNames[i];
        }
      }
    },
    
    setupLocalColumn(item) {
      // Inject translations for static items
      switch (item.headerName) {
        case "Country":
          item.headerName = this.$t('Webportal.CX.Country');
          break;
        case "City":
          item.headerName = this.$t('Webportal.CX.City');
          break;
        case "Postal Code":
          item.headerName = this.$t('Webportal.CX.ZipCode');
          break;
        case "Address":
          item.headerName = this.$t('Webportal.CX.Address');
          break;
        case "Details":
          item.headerName = this.$t('Webportal.CX.Details');
          break;
        case "Location":
          item.headerName = this.$t('Webportal.CX.Location');
          break;
        case "Image":
          item.headerName = this.$t('Webportal.CX.Image');
          break;
        case "JLR Number":
          item.headerName = this.$t('Webportal.CX.JLR-Number');
          break;     
      }

      if (!item.path && !item.columnCount) 
        return item;

      this.setupColumnDefinitionsIfMissing(item);

      const { path, columnsCount, ...result } = item;
      return this.setupMultiColumns(item, 
        { ...result, children: [] });
    },

    setupMultiColumns(item, result) {
      for (let i = 0; i < item.columns.length; i++) {
        let col = this.setupSingleColumn(item, item.columns[i], i);
        result.children.unshift(col);
      }
      return result;
    },

    setupSingleColumn(item, col, localIndex) {
      let column = {  
        valueGetter: (params) => { 
          try {
            if (!params.data) return;
            let rowProperty = jpath.query(params.data, item.path);
            if (!Array.isArray(rowProperty)) 
              return rowProperty;
            if (!rowProperty || rowProperty.length !== 1) 
              return;
            if (Number(rowProperty[0]) === rowProperty[0] || _.isString(rowProperty[0]) || rowProperty[0] === null) 
              return rowProperty[0];
            
            let rowResult = rowProperty[0][localIndex];
            if (!rowResult) 
              return; 
            if (rowResult?.value === undefined && 
                rowResult?.WaveResult === undefined && 
                rowResult?.QuestionResult === undefined && 
                rowResult?.result === undefined) {
            }

            return rowResult?.value 
                || rowResult?.WaveResult 
                || rowResult?.QuestionResult 
                || rowResult?.result;
                
          } catch (error) {
            return `Path N/A!: ${col}`;
          }
        },
        aggFunc: this.avgAggregation || (_ => { return 'RESULT' }),
        cellRenderer: col.render ? `${col.render}CellRenderer` : (item.cellRenderer ? item.cellRenderer : null),
        cellRendererParams: {
          localIndex: localIndex,
          path: item.path,
          canHaveAccomplished: this.canHaveAccomplished
        },
        cellStyle: {'text-align': 'center', 'text-align': '-webkit-center' },
        columnGroupShow: col.show,
        field: `[${col.key}][${localIndex}].value`, 
        filter: 'agMultiColumnFilter',
        filterParams: this.provideFilterParams(item, localIndex),
        headerName: col.headerName, // this.provideColumnHeaderText(item, col, localIndex),
        headerTooltip: item, 
        tooltipComponent: 'thresholdTooltip',
        valueFormatter: col.format ? this[`${col.format}ValueFormatter`] : null
      };
      return column;
    },
    
    provideFilterParams(item, localindex) {
      let filterParams = {
        filters: [{
          filter: 'agNumberColumnFilter',
          filterParams: { defaultOption: 'startsWith' },
        }]
      };

      if (this.canHaveAccomplished && item.group !== "Scores" && localindex === 0) {
        filterParams.filters.push(
          { 
            filter: 'accomplishedFilter',
            filterParams: { 
              localIndex: localindex,
              path: item.path
            }
          }
        )
      }
          
      return filterParams;
    },

    provideColumnHeaderText(item, col, localIndex) {
      // Hack fix header translations
      switch (col.text) {
        case "Wave 1":
        case "Wave 2":
        case "Wave 3":
          return `${this.$t('Webportal.CX.Wave')} ${col.text.split(" ")[1]}`;
        
        case "Check 1":
        case "Check 2":
        case "Check 3":
          return `${this.$t('Webportal.CX.Check')} ${col.text.split(" ")[1]}`;
            
        case "Current Value":
          return this.$t('Webportal.CX.Dashboard.Exploration.CurrentValue');
          
        case "Current Check":
        case "Last Check":
          return this.$t('Webportal.CX.Dashboard.Exploration.LastCheck');

        default:
          break;
      }

      // aus Text, MDS-Key oder Datum den richtigen Wert ausgeben
      if (col.text.startsWith('Webportal.')) {
        return this.$t(col.text);
      } else if (col.text.length == 10 && col.text[4] == '-' && col.text[7] == '-') {
        // Datum (monat / Jahr) landesspezifisch
        return moment(col.text).format('MMMM YYYY');
      } else {
        return col.text;
      }
    },

    setupCxMultiColumns(column) {
      for (let i = 0; i < column.columns.length; i++) {
        let col = this.setupSingleColumn(column, column.columns[i], i);
        column.children.unshift(col);
      }
    },

    cxGetDataSource(tableId) {
      if (tableId === 'market') {
        return this.countryValues;
      } else if (tableId === 'retailer') {
        return this.retailerValues;
      } else {
        console.error("FAIL")
        return [];
      }
    },

    cxCreateMarginColumnDefinitions(margins) {
      let columns = [];

      for (const margin of margins) {
        let column = {
          category_TranslationString: null,
          columnCount: margin.results.length,
          columnNames: margin.results.map(x => `Q${x.quarter}/${x.year}`),
          format: margin.format,
          group: margin.group,
          indent: 1,
          isHeader: false,
          key: margin.name,
          path: "$.results[?(@.name==\"" + margin.name + "\")].results",
          render: margin.render,
          selected: true,
          targets : null,
          text: margin.name,
          thresholds: []
        };

        columns.push(column);
      }
      return columns;
    },

    setupCxTableStructure(rawColumns, tableId) {
      // remove header columns, we don't need them anymore in CX
      let columns = rawColumns;

      // Get all the Margin columns
      let items = this.cxGetDataSource(tableId);
      let marginColumnDefinitions = [];
      if (items.AggregatedData) { 
        let uniqueMarginValues = items.AggregatedData.flatMap(data => data.results)
          .reduce((unique, item) => {
            if (item.group === 'Margin' && !unique.some(uniqueItem => uniqueItem.name === item.name)) {
              unique.push(item);
            }
            return unique;
        }, []);
        marginColumnDefinitions = this.cxCreateMarginColumnDefinitions(uniqueMarginValues);
        columns.push(...marginColumnDefinitions);
      }

      // set up all columns from api
      for (const column of columns) {
        column.children = [];
        this.setupColumnDefinitionsIfMissing(column);
        this.setupCxMultiColumns(column);
        // Set header name
        if (!column.headerName) {
          column.headerName = column.text;
        }
        // set all columns to hidden
        for(const child of column.children) {
          child.hide = true;
        }

        // Set up threshold-texts for all column-tooltips
        if (!_.isEmpty(column.thresholds)) {
          for (let j = 0; j < column.thresholds.length; j++) {
            const th = column.thresholds[j];
            if (_.isEmpty(th.blabla)) {
              th.niceText = this.getThresholdText(columns, th, '%', ' - ');
            }
          }
        }
      }

      // enable and order visible columns
      this.setCxProps(columns, "CX Score", { sortOrder: 0 }, { hide: false });
      this.setCxProps(columns, "Exploration Score", { sortOrder: 1 }, { hide: false });
      this.setCxProps(columns, "Mystery Shopping", { sortOrder: 2 }, { hide: false });
      
      this.enableAllMatchingItems(columns, "DHC", undefined, { hide: false });

      // On retailer table, show Lead Management columns
      if (tableId === 'retailer') {
        let sortIndex = 3;
        for (const item of marginColumnDefinitions) {
          this.setCxProps(columns, item.text, { sortOrder: sortIndex }, { hide: false });
        }
      }

      columns = _.sortBy(columns, ['sortOrder', 'text']);
      return columns;
    },

    enableAllMatchingItems(columns, text, properties, childProperties) {
      let matchingColumns = columns.filter(x => x.text.includes(text));
      for (const column of matchingColumns) {
        this.setCxColumnProps(column, properties, childProperties);
      }
    },

    setCxProps(columns, text, properties, childProperties) {
      let column = columns.find(x => x.text === text);
      this.setCxColumnProps(column, properties, childProperties);
    },

    setCxColumnProps(column, properties, childProperties) {
      if (!column) return;

      // set all properties on column(group)
      for (const key in properties) {
        column[key] = properties[key];
      }
      // set all properties on all children
      for (const child of column.children) {
        for (const key in childProperties) {
          child[key] = childProperties[key];
        }
      }
    },

    getCxTableStructure(token, parameters) {
      return DashboardService.getTableColumns(parameters, token)
        .then(response => {
          // Setup standard columns
          let items = [...this.columnDefs].map(this.setupLocalColumn);
          // Setup custom (market specific) columns
          // TODO: Include 
          
          let customColumns = this.setupCxTableStructure(response.data, parameters.tableId);
          this.cxColumns = items.concat(customColumns);
        })
        .catch((err) => console.warn(err))
        .finally(() => {
        });
    }
  }
}